<script setup>
import {computed} from "vue";
import {getXaxisDates} from "@/charts";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ReportingEmpty from "@/components/reporting/partials/ReportingEmpty.vue";
import useTrafficStore from "@/composables/useTrafficStore";
import useAccountMissing from "@/composables/useAccountMissing";
import Reporting from "@/services/reporting";
import {useQueries} from "@tanstack/vue-query";
import useQueriesState from "@/composables/useQueriesState";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import _ from "lodash";
import SourceGSC from "@/components/reporting/partials/SourceGSC.vue";
import LineChart from "@/components/global-themed/charts/LineChart.vue";

const {fromDate, toDate, compareFromDate, compareToDate} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchClicksAndImpressions = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchClicksAndImpressions(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const results = useQueries({
  queries: [
    {
      queryKey: ['reporting-clicks-and-imps', 'reporting', fromDate, toDate],
      queryFn: async () => fetchClicksAndImpressions(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-clicks-and-imps', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchClicksAndImpressions(compareFromDate.value, compareToDate.value),
    },
  ],
});

const {data, comparedData, isLoading: loading, isError} = useQueriesState(results);

const xAxis = computed(() => getXaxisDates(data.value, comparedData.value));

const mapValuesToSeries = (values, property) => {
  if (!values) return [];

  return xAxis.value?.map((date, index) => {
    const row = values[index] || {};
    return {
      x: new Date(date).getTime(),
      y: row[property] ?? null,
    }
  });
}

const series = computed(() => {
  const series = [
    {
      name: 'Clicks',
      data: mapValuesToSeries(data.value, 'clicks'),
    },
    {
      name: 'Impressions',
      data: mapValuesToSeries(data.value, 'impressions'),
    },
  ];

  if (!comparedData.value) {
    return series;
  }

  return [
    ...series,
    {
      name: 'Previous Clicks',
      data: mapValuesToSeries(comparedData.value, 'clicks'),
    },
    {
      name: 'Previous Impressions',
      data: mapValuesToSeries(comparedData.value, 'impressions'),
    },
  ];
});

const combinedData = computed(() => [
  ...(data.value || []),
  ...(comparedData.value || [])
]);

const maxClicks = computed(() => _.ceil(_.maxBy(combinedData.value, 'clicks')?.clicks || 0, -2));

const maxImpressions = computed(() => _.ceil(_.maxBy(combinedData.value, 'impressions')?.impressions || 0, -3));

const yAxis = computed(() => {
  const yAxisDefaults = {
    labels: {
      style: {
        cssClass: 'apexcharts-yaxis-label',
      },
      formatter: (value) => value?.toFixed(0),
    },
    min: 0,
  };

  const yAxis = [
    {
      ...yAxisDefaults,
      title: {
        text: 'Clicks',
      },
      opposite: true,
      showAlways: true,
      max: maxClicks.value,
    },
    {
      ...yAxisDefaults,
      title: {
        text: 'Impressions',
      },
      showAlways: true,
      max: maxImpressions.value,
    },
  ];
  if (!comparedData.value) return yAxis;

  return [
    ...yAxis,
    // Pad y-axis when compare data is shown
    {
      ...yAxisDefaults,
      seriesName: 'Previous Clicks',
      opposite: true,
      show: false,
      max: maxClicks.value,
    },
    {
      ...yAxisDefaults,
      seriesName: 'Previous Impressions',
      show: false,
      max: maxImpressions.value,
    },
  ];
});


const options = computed(() => ({
  xaxis: {
    type: 'datetime',
    tooltip: {
      enabled: false
    },
    labels: {
      style: {
        cssClass: 'apexcharts-xaxis-label',
      }
    }
  },
  yaxis: yAxis.value,
}));

</script>

<template>
  <div>
    <h3 class="tw-text-xl tw-text-font-primary tw-font-bold tw-font-sans tw-mb-6">
      Clicks and Impressions
    </h3>

    <div
      v-if="!data?.length"
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-w-full tw-mb-8"
    >
      <LoadingBlock v-if="loading" />
      <ConnectAccount v-else-if="isAccountMissing" />
      <ErrorBlock v-else-if="isError" />
      <ReportingEmpty v-else />
      <SourceGSC />
    </div>
    <div
      v-else
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-mt-16 tw-mb-8"
    >
      <LineChart
        :series="series"
        :options="options"
      />
      <SourceGSC />
    </div>
  </div>
</template>
